import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
	Archive,
	Bell,
	Briefcase,
	Building2,
	CalendarClock,
	CheckCircle2,
	Database,
	FileText,
	Flag,
	Folder,
	History,
	Inbox,
	KeyRound,
	LayoutDashboard,
	LifeBuoy,
	LogOut,
	MessageSquare,
	Network,
	Upload,
	UserCheck,
	UserCog,
	UserRound,
	Users,
} from "lucide-react";
import { cn } from "../../../lib/utils";

/** PeerMed sidebar icons, keyed by nav label (fallback: Folder). */
const NAV_ICONS = {
	"Dashboard": LayoutDashboard,
	"User Management": UserCog,
	"Case Directory": Network,
	"Case Log": History,
	"Password Management": KeyRound,
	"Draft Cases": FileText,
	"Published Cases": Folder,
	"Scheduled Cases": CalendarClock,
	"Pending Review": Inbox,
	"Pending Review by me": UserCheck,
	"For Discussion": MessageSquare,
	"My Cases": Folder,
	"Approved Cases": CheckCircle2,
	"Cases for Archive": Archive,
	"Archived Cases": Archive,
	"Flagged Cases": Flag,
	"Comorbidities": Database,
	"Procedures": Folder,
	"Complications": LifeBuoy,
	"Position": Briefcase,
	"Departments": Building2,
	"Group": Users,
	"Notifications": Bell,
	"Upload Case": Upload,
	"Support Request": LifeBuoy,
	"Profile": UserRound,
	"Log Out": LogOut,
};

/**
 * Renders a role's routes array (path/label/onClick entries, "Divider" rows)
 * in the PeerMed sidebar style. Route arrays and permission checks stay in
 * the per-role nav files — this only handles presentation.
 */
const NavItems = ({ routes }) => {
	const location = useLocation();
	let firstGroup = true;

	// Drop empty sections: consecutive dividers (e.g. Password Management →
	// Divider → no case links → Divider → Comorbidities) and edge dividers.
	const cleanedRoutes = [];
	for (const route of routes) {
		if (route.label === "Divider") {
			if (cleanedRoutes.length === 0) continue;
			if (cleanedRoutes[cleanedRoutes.length - 1].label === "Divider") continue;
			cleanedRoutes.push(route);
			continue;
		}
		cleanedRoutes.push(route);
	}
	while (
		cleanedRoutes.length > 0 &&
		cleanedRoutes[cleanedRoutes.length - 1].label === "Divider"
	) {
		cleanedRoutes.pop();
	}

	return (
		<>
			{cleanedRoutes.map((route, index) => {
				if (route.label === "Divider") {
					firstGroup = false;
					return (
						<div key={index} className="mx-3 my-3 border-t border-parch-100" aria-hidden />
					);
				}

				const Icon = NAV_ICONS[route.label] ?? Folder;
				const active = location.pathname === route.path;
				const className = cn("nav-item", active && "nav-item-active");
				const showMainLabel = firstGroup && index === 0;

				return (
					<React.Fragment key={index}>
						{showMainLabel && <p className="section-label px-3 pt-2 pb-1">Main</p>}
						{route.onClick ? (
							<a href="#logout" className={className} onClick={route.onClick}>
								<span className="nav-item-bar" />
								<Icon className="h-4 w-4" />
								<span className="flex-1">{route.label}</span>
							</a>
						) : (
							<Link to={route.path} className={className}>
								<span className="nav-item-bar" />
								<Icon className="h-4 w-4" />
								<span className="flex-1">{route.label}</span>
							</Link>
						)}
					</React.Fragment>
				);
			})}
		</>
	);
};

export default NavItems;
